import React from 'react';
import { motion } from 'framer-motion';
import { Search, Calendar, MapPin, Users, Star } from 'lucide-react';
import { useComponentTranslations } from '../hooks/useTranslations';

const Hero = () => {
  const { t, loading } = useComponentTranslations('hero');

  const scrollToSection = (sectionId) => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
  };

  if (loading) {
    return (
      <section id="hero" className="relative min-h-screen flex items-center justify-center overflow-hidden pt-32 sm:pt-40 z-10">
        <div className="absolute inset-0 bg-gradient-to-r from-evergreen/80 via-evergreen/70 to-dream-teal/80"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="mb-8">
            <div className="h-16 bg-white/20 rounded-lg animate-pulse mb-6"></div>
            <div className="h-8 bg-white/20 rounded-lg animate-pulse mb-8"></div>
            <div className="flex justify-center space-x-4">
              <div className="h-12 w-32 bg-white/20 rounded-lg animate-pulse"></div>
              <div className="h-12 w-32 bg-white/20 rounded-lg animate-pulse"></div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="hero" className="relative min-h-screen flex items-center justify-center overflow-hidden pb-20 mb-10 pt-32 sm:pt-40 z-10">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0">
        <img 
          src="https://images.unsplash.com/photo-1573497619860-6d82917e4ec8"
          alt="Management Consulting Team Meeting"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-evergreen/80 via-evergreen/70 to-dream-teal/80"></div>
      </div>

      {/* Content */}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          className="mb-8"
        >
          <h1 className="text-3xl xs:text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-bold text-white mb-6 leading-tight">
            {t('title', 'Operational Excellence in')}
            <span className="block bg-gradient-to-r from-dream-teal to-coral bg-clip-text text-transparent">
              {t('highlight', 'Business Management')}
            </span>
          </h1>
          
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5, duration: 1 }}
            className="text-lg xs:text-xl sm:text-2xl md:text-3xl lg:text-4xl text-teal-100 mb-4 max-w-4xl mx-auto leading-relaxed"
          >
            {t('subtitle', 'Empowering businesses through strategic management consulting, operational excellence, and tailored business support solutions.')}
          </motion.p>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7, duration: 1 }}
            className="text-2xl xs:text-3xl sm:text-4xl md:text-5xl font-bold text-coral mb-8"
          >
            {t('tagline', 'Streamline. Strengthen. Succeed.')}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.8 }}
            className="flex flex-col sm:flex-row justify-center items-center space-y-4 sm:space-y-0 sm:space-x-6"
          >
            <button
              onClick={() => scrollToSection('services')}
              className="group bg-gradient-to-r from-dream-teal to-evergreen text-white px-6 xs:px-8 sm:px-10 md:px-12 py-3 xs:py-4 sm:py-5 rounded-full font-semibold text-base xs:text-lg sm:text-xl md:text-2xl shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105 border-2 border-transparent hover:border-white/30"
            >
              <span className="flex items-center">
                {t('cta_primary', 'Our Services')}
                <motion.div
                  className="ml-2"
                  animate={{ x: [0, 5, 0] }}
                  transition={{ repeat: Infinity, duration: 1.5 }}
                >
                  →
                </motion.div>
              </span>
            </button>

            <button
              onClick={() => scrollToSection('about')}
              className="group border-2 border-white text-white px-6 xs:px-8 sm:px-10 md:px-12 py-3 xs:py-4 sm:py-5 rounded-full font-semibold text-base xs:text-lg sm:text-xl md:text-2xl hover:bg-white hover:text-gray-900 transition-all duration-300 transform hover:scale-105"
            >
              {t('cta_secondary', 'Learn About Us')}
            </button>
          </motion.div>
        </motion.div>

        {/* Key Features */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.5, duration: 1 }}
          className="grid grid-cols-2 lg:grid-cols-4 gap-4 xs:gap-6 sm:gap-8 mt-12 xs:mt-16 sm:mt-20"
        >
          {[
            { 
              icon: Users, 
              title: t('process', 'Process Optimization'),
              key: 'process'
            },
            { 
              icon: Search, 
              title: t('strategic', 'Strategic Consulting'),
              key: 'strategic'
            },
            { 
              icon: Calendar, 
              title: t('workflow', 'Workflow Management'),
              key: 'workflow'
            },
            { 
              icon: Star, 
              title: t('excellence', 'Operational Excellence'),
              key: 'excellence'
            }
          ].map((feature, index) => (
            <motion.div
              key={feature.key}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.8 + index * 0.1, duration: 0.6 }}
              className="text-center group"
            >
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 xs:p-6 sm:p-8 hover:bg-white/20 transition-all duration-300 transform hover:scale-105 border border-white/20">
                <feature.icon className="w-6 h-6 xs:w-8 xs:h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 text-teal-300 mx-auto mb-3 xs:mb-4 group-hover:text-white transition-colors duration-300" />
                <h3 className="text-white font-semibold text-xs xs:text-sm sm:text-base md:text-lg">
                  {feature.title}
                </h3>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2.5, duration: 1 }}
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ repeat: Infinity, duration: 2 }}
            className="w-6 h-10 border-2 border-white rounded-full flex justify-center"
          >
            <motion.div
              animate={{ y: [0, 12, 0] }}
              transition={{ repeat: Infinity, duration: 2 }}
              className="w-1 h-3 bg-white rounded-full mt-2"
            />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;